import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Brain, Plus, Trash2, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { useDecisionAnalysisStore } from '../stores/decisionAnalysisStore'
import { analyzeWithContextStepByStep } from '../services/api'
import { useAIUsageStore } from '../stores/aiUsageStore'
import { useAuthStore } from '../stores/authStore'
import { DecisionTemplates } from '../components/DecisionTemplates'
import { ContextAnalysis } from '../components/ContextAnalysis'
import { AnalysisProgress } from '../components/AnalysisProgress'


export const ContextAnalysisPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const {
    decisionTitle,
    decisionDescription,
    options,
    criteria,
    isAnalyzing,
    analysisResult,
    analysisSteps,
    currentAnalysisStep,
    contextProfiles,
    activeProfileId,
    setDecisionTitle,
    setDecisionDescription,
    addOption,
    removeOption,
    updateOption,
    addCriteria,
    removeCriteria,
    updateCriteria,
    setIsAnalyzing,
    setAnalysisResult,
    resetDecision,
    addToHistory,
    setOptions,
    setCriteria,
    initializeContextAnalysisSteps,
    updateAnalysisStep,
    setCurrentAnalysisStep,
    addContextProfile,
    updateContextProfile,
    removeContextProfile,
    setActiveProfile
  } = useDecisionAnalysisStore()

  const { user } = useAuthStore()
  const { canUseAI, incrementUsage, getRemainingUsage } = useAIUsageStore()
  const [showTemplates, setShowTemplates] = useState(false)

  const startAnalysis = async () => {
    if (!user && !canUseAI()) {
      alert(`You have ${getRemainingUsage()} AI calls remaining. Please sign in for unlimited access.`)
      return
    }

    if (!activeProfileId) {
      alert('Please select or create a context profile first.')
      return
    }

    setIsAnalyzing(true)
    initializeContextAnalysisSteps()
    
    try {
      if (!user) {
        incrementUsage()
      }

      const activeProfile = contextProfiles.find(p => p.id === activeProfileId)
      const result = await analyzeWithContextStepByStep(
        {
          decisionTitle,
          decisionDescription,
          options,
          criteria,
          contextProfile: activeProfile
        },
        (stepId: string, status: 'active' | 'completed' | 'error') => {
          updateAnalysisStep(stepId, status)
          if (status === 'active') {
            setCurrentAnalysisStep(stepId)
          }
        },
        (stepId: string, result: any) => {
          console.log(`Step ${stepId} completed:`, result)
        }
      )

      setAnalysisResult(result.analysis)
      addToHistory({
        title: decisionTitle,
        description: decisionDescription,
        options,
        criteria,
        result: result.analysis
      })
    } catch (error) {
      console.error('Analysis failed:', error)
      alert('Analysis failed. Please try again.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleTemplateSelect = (template: any) => {
    setDecisionTitle(template.title)
    setDecisionDescription(template.fullDescription)

    const templateOptions = template.options.map((opt: string) => ({
      id: Date.now().toString() + Math.random(),
      text: opt,
      pros: [],
      cons: [],
      score: 0
    }))
    const templateCriteria = template.criteria.map((c: any) => ({
      id: Date.now().toString() + Math.random(),
      name: c.name,
      weight: c.weight,
      description: c.description
    }))
    setOptions(templateOptions)
    setCriteria(templateCriteria)
    setShowTemplates(false)
  }

  const renderAnalysisForm = () => (
    <div className="space-y-8">
      {/* Decision Definition */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">What decision are you trying to make?</h3>
          <input
            type="text"
            value={decisionTitle}
            onChange={(e) => setDecisionTitle(e.target.value)}
            placeholder="e.g., Should I accept this job offer?"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Provide more context</h3>
          <textarea
            value={decisionDescription}
            onChange={(e) => setDecisionDescription(e.target.value)}
            placeholder="Describe your situation, goals, and any relevant factors..."
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">What are your options?</h3>
          <button
            onClick={addOption}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Option</span>
          </button>
        </div>
        <div className="space-y-4">
          {options.map((option, index) => (
            <div key={option.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">Option {index + 1}</span>
                <button
                  onClick={() => removeOption(option.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(option.id, 'text', e.target.value)}
                placeholder="Describe this option..."
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          ))}
          {options.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Add your decision options to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Criteria */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">What criteria matter to you?</h3>
          <button
            onClick={addCriteria}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Criteria</span>
          </button>
        </div>
        <div className="space-y-4">
          {criteria.map((criterion, index) => (
            <div key={criterion.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">Criteria {index + 1}</span>
                <button
                  onClick={() => removeCriteria(criterion.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                <input
                  type="text"
                  value={criterion.name}
                  onChange={(e) => updateCriteria(criterion.id, 'name', e.target.value)}
                  placeholder="e.g., Salary, Work-life balance, Growth opportunities"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <textarea
                  value={criterion.description}
                  onChange={(e) => updateCriteria(criterion.id, 'description', e.target.value)}
                  placeholder="Why is this important to you?"
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Importance (1-10): {criterion.weight}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={criterion.weight}
                    onChange={(e) => updateCriteria(criterion.id, 'weight', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          ))}
          {criteria.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Add criteria that matter to your decision</p>
            </div>
          )}
        </div>
      </div>

      {/* Context Profiles */}
      <div className="space-y-6">
        <ContextAnalysis
          profiles={contextProfiles}
          activeProfileId={activeProfileId}
          onAddProfile={addContextProfile}
          onUpdateProfile={updateContextProfile}
          onRemoveProfile={removeContextProfile}
          onSetActiveProfile={setActiveProfile}
        />
      </div>

      {/* Analysis Button */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-purple-900 mb-2">Ready for Context Analysis</h3>
        <p className="text-purple-800 mb-4">
          We'll analyze your decision considering your personal context and preferences for more personalized recommendations.
        </p>
        
        <div className="space-y-4">
          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <h4 className="font-medium text-gray-900 mb-2">Decision Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 mb-1"><strong>Title:</strong></p>
                <p className="text-gray-900">{decisionTitle || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1"><strong>Options:</strong></p>
                <p className="text-gray-900">{options.length} option{options.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-600 mb-1"><strong>Description:</strong></p>
                <p className="text-gray-900">{decisionDescription || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1"><strong>Criteria:</strong></p>
                <p className="text-gray-900">{criteria.length} criterion{criteria.length !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1"><strong>Active Profile:</strong></p>
                <p className={`${activeProfileId ? 'text-green-600' : 'text-red-600'}`}>
                  {activeProfileId ? 'Selected' : 'Not selected'}
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={startAnalysis}
            disabled={isAnalyzing || options.length === 0 || criteria.length === 0 || !activeProfileId}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Context Analysis in Progress...</span>
              </>
            ) : (
              <>
                <Brain className="w-5 h-5" />
                <span>Start Context Analysis</span>
              </>
            )}
          </button>
          
          {!isAnalyzing && (options.length === 0 || criteria.length === 0 || !activeProfileId) && (
            <div className="text-sm text-gray-500 text-center">
              {options.length === 0 && <p>• Add at least one option</p>}
              {criteria.length === 0 && <p>• Add at least one criterion</p>}
              {!activeProfileId && <p>• Select or create a context profile</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderAnalysisResult = () => {
    if (isAnalyzing) {
      return (
        <AnalysisProgress 
          steps={analysisSteps}
          currentStep={currentAnalysisStep}
          analysisType="context"
        />
      )
    }
    
    if (!analysisResult) return null
    
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-medium text-green-900">Context Analysis Complete</h3>
          </div>
          <p className="text-green-800">
            Based on your personal context and criteria, here's our AI-powered recommendation.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-xl font-medium text-gray-900 mb-4">Recommended Option</h4>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <p className="text-purple-900 font-medium">{analysisResult.recommendedOption}</p>
          </div>
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-sm text-gray-600">Confidence Level:</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-500 ease-out" 
                style={{ width: `${analysisResult.confidence}%` }}
              ></div>
            </div>
            <span className="text-sm font-medium text-gray-900">{analysisResult.confidence}%</span>
          </div>
          <p className="text-gray-700">{analysisResult.reasoning}</p>
        </div>

        {/* Detailed Analysis */}
        {analysisResult.detailedAnalysis && (
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900">Detailed Analysis</h4>
            {analysisResult.detailedAnalysis.map((analysis: any, index: number) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-2">{analysis.option}</h5>
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm text-gray-600">Score:</span>
                  <span className="text-sm font-medium text-gray-900">{analysis.score}/100</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h6 className="text-sm font-medium text-green-700 mb-1">Strengths</h6>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {analysis.strengths.map((strength: string, i: number) => (
                        <li key={i} className="flex items-center space-x-1">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h6 className="text-sm font-medium text-red-700 mb-1">Weaknesses</h6>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {analysis.weaknesses.map((weakness: string, i: number) => (
                        <li key={i} className="flex items-center space-x-1">
                          <XCircle className="w-3 h-3 text-red-600" />
                          <span>{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Personalized Insights */}
        {analysisResult.personalizedInsights && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="text-lg font-medium text-purple-900 mb-3">Personalized Insights</h4>
            <ul className="space-y-2">
              {analysisResult.personalizedInsights.map((insight: string, i: number) => (
                <li key={i} className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-purple-800">{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risk Assessment */}
        {analysisResult.riskAssessment && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-lg font-medium text-yellow-900 mb-3">Risk Assessment</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-yellow-800">Overall Risk:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  analysisResult.riskAssessment.overallRisk === 'low' ? 'bg-green-100 text-green-800' :
                  analysisResult.riskAssessment.overallRisk === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {analysisResult.riskAssessment.overallRisk.toUpperCase()}
                </span>
              </div>
              {analysisResult.riskAssessment.risks && (
                <div>
                  <h5 className="text-sm font-medium text-yellow-800 mb-1">Key Risks:</h5>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {analysisResult.riskAssessment.risks.map((risk: string, i: number) => (
                      <li key={i} className="flex items-center space-x-1">
                        <AlertCircle className="w-3 h-3" />
                        <span>{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {analysisResult.riskAssessment.mitigation && (
                <div>
                  <h5 className="text-sm font-medium text-yellow-800 mb-1">Mitigation Strategies:</h5>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {analysisResult.riskAssessment.mitigation.map((strategy: string, i: number) => (
                      <li key={i} className="flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>{strategy}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Next Steps */}
        {analysisResult.nextSteps && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-lg font-medium text-blue-900 mb-3">Recommended Next Steps</h4>
            <div className="space-y-2">
              {analysisResult.nextSteps.map((step: string, i: number) => (
                <div key={i} className="flex items-start space-x-2">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-blue-800">{step}</span>
                </div>
              ))}
            </div>
            {analysisResult.timeline && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <span className="text-sm font-medium text-blue-800">Timeline: </span>
                <span className="text-sm text-blue-700">{analysisResult.timeline}</span>
              </div>
            )}
          </div>
        )}

        {/* Contextual Factors */}
        {analysisResult.contextualFactors && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-lg font-medium text-blue-900 mb-3">Contextual Factors Considered</h4>
            <ul className="space-y-2">
              {analysisResult.contextualFactors.map((factor: string, i: number) => (
                <li key={i} className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-blue-800">{factor}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex space-x-4">
          <button
            onClick={() => {
              resetDecision()
            }}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Start New Analysis
          </button>
        </div>
      </div>
    )
  }

  const handleShowTemplates = () => {
    resetDecision()
    setShowTemplates(true)
  }

  const handleNewAnalysis = () => {
    resetDecision()
    setShowTemplates(false)
  }

  return (
    <>
      <Helmet>
        <title>Context Analysis - AI Decision Maker</title>
        <meta name="description" content="Support more personalized analysis, can add multiple profiles" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Context Analysis</h1>
              <p className="text-gray-600">
                Support more personalized analysis, can add multiple profiles
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleNewAnalysis}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" /><span>New</span>
              </button>
              <button
                onClick={handleShowTemplates}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FileText className="w-4 h-4" /><span>Templates</span>
              </button>
            </div>
          </div>
        </div>

        {showTemplates ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <DecisionTemplates onSelectTemplate={handleTemplateSelect} />
          </div>
        ) : (analysisResult || isAnalyzing) ? (
          renderAnalysisResult()
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {renderAnalysisForm()}
          </div>
        )}
      </div>
    </>
  )
} 